import { useEffect } from "react";
import { Link, useSearch } from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { betterAuthErrorText } from "lib/auth/authClient";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useResendVerificationEmail } from "lib/hooks/api/auth/useResendVerificationEmail";
import { Card, Page } from "./authPageStyles";
import { StotlesLogo } from "./StotlesLogo";

// better-auth's /verify-email redirects here with `?error=<CODE>` when it can't honour the link. The
// codes are the keys of its BASE_ERROR_CODES (uppercase — see defineErrorCodes), matched
// case-insensitively so a change of convention upstream doesn't silently fall through to the default.
const VERIFICATION_ERRORS: Record<string, string> = {
  token_expired: "That verification link has expired.",
  invalid_token:
    "That link isn't valid. It may have been cut off or altered, so try copying the full link from the email.",
  user_not_found: "We couldn't find an account for that link.",
  // Only reachable for an email-*change* link opened while signed in as a different user; a signup
  // or social verification link has no session check at all, so confirming on another device works.
  invalid_user: "That link was issued for a different account. Sign out first, then open it again.",
};

const DEFAULT_VERIFICATION_ERROR = "We couldn't confirm your email address from that link.";

/** Copy for a verification failure code. Unknown codes fall back to a generic message. */
export function verificationErrorText(error: string | undefined): string {
  if (!error) return DEFAULT_VERIFICATION_ERROR;
  return VERIFICATION_ERRORS[error.toLowerCase()] ?? DEFAULT_VERIFICATION_ERROR;
}

/**
 * Landing page for an emailed verification link, for both outcomes — better-auth's /verify-email has
 * a single `callbackURL` and appends `?error=<CODE>` on failure, so one page has to serve both.
 *
 * On success there's nothing to show: `autoSignInAfterVerification` means the user already holds a
 * session by the time they arrive, so we forward them straight into the app. On failure we explain
 * which way it failed and offer a fresh link, using the `email` we put on the URL when the original
 * was sent (see verificationCallbackUrl).
 */
export function BetterAuthEmailConfirmedPage() {
  // Bounded + sanitised by the route's validateEmailConfirmedSearch. `strict: false` lets these
  // resolve to undefined when rendered outside the route (e.g. tests).
  const { email, redirect, error } = useSearch({ strict: false });

  const {
    mutate: resendVerificationEmail,
    isLoading: isResending,
    isSuccess: hasResent,
    isError: isResendError,
    error: resendError,
  } = useResendVerificationEmail();

  useEffect(() => {
    if (error) return;
    // A full navigation, not a router push: the destination has to boot with the new session so the
    // Rails shell and the root-route guards see it (same reasoning as the social callback page).
    window.location.assign(redirect ?? "/");
  }, [error, redirect]);

  if (!error) {
    return <CentredSpinner />;
  }

  return (
    <Page>
      <Card centred>
        <StotlesLogo />
        <Text h1 color="sysDestructiveDefault">
          We couldn&apos;t confirm your email
        </Text>
        <Text color="sysTextSecondary">{verificationErrorText(error)}</Text>
        {email && (
          <>
            <Text color="sysTextSecondary">
              We can send a new link to <strong>{email}</strong>.
            </Text>
            <Button
              buttonType="primary"
              fullWidth
              loading={isResending}
              disabled={hasResent}
              onClick={() => resendVerificationEmail({ email, redirect })}
            >
              {hasResent ? "Verification email sent" : "Send a new link"}
            </Button>
            {isResendError && (
              <Text color="sysDestructiveDefault">
                {betterAuthErrorText(resendError, "Could not send a new link")}
              </Text>
            )}
          </>
        )}
        <Text color="sysTextSecondary">
          <Link to="/auth/login">Back to log in</Link>
        </Text>
      </Card>
    </Page>
  );
}
